import React, {useEffect, useState} from 'react';
import {useQuery, useQueryClient} from '@tanstack/react-query';
import {useParams} from 'react-router-dom';
import {proyectosApi} from '../../../api/api';
import {ProjectFormLayout, type ProjectFormSectionNavItem} from '../projects/ProjectFormLayout';
import {ProjectReadOnlyProvider} from '../projects/projectForm.helpers';
import {SectionSaveCoordinatorProvider, useSectionSaveCoordinator} from '../projects/SectionSaveCoordinator';
import type {ProyectoDTO} from '../../../api/Objetos';
import {InformacionGeneralSection} from '../projects/projectSections/InformacionGeneralSection';
import {RequerimientosSection} from '../projects/projectSections/RequerimientosSection';
import {AutorizacionesSection} from '../projects/projectSections/AutorizacionesSection';
import {DatosContratosSection} from '../projects/projectSections/DatosContratosSection';
import {DatosContablesSection} from '../projects/projectSections/DatosContablesSection';
import {SeguimientoPlataformaSection} from '../projects/projectSections/SeguimientoPlataformaSection';
import {SeguimientoCoffeSection} from '../projects/projectSections/SeguimientoCoffeSection';
import {CertificacionesDeObraSection} from '../projects/projectSections/CertificacionesDeObraSection';
import {HitosYObjetivosSection} from '../projects/projectSections/HitosYObjetivosSection';
import {ParticipacionSection} from '../projects/projectSections/ParticipacionSection';
import {InformesSection} from '../projects/projectSections/InformesSection';
import {ComunicacionSection} from '../projects/projectSections/ComunicacionSection';
import {ReunionesSection} from '../projects/projectSections/ReunionesSection';
import {PlazosSection} from '../projects/projectSections/PlazosSection';
import {AccionesSeguimientoSection} from '../projects/projectSections/AccionesSeguimientoSection';
import '../ui/adminUi.css';
import {useAuth} from '../../../app/providers/AuthProvider';
import {useBloqueoProyecto} from '../../../hooks/useBloqueoProyecto';
import {LockBanner} from '../ui/LockBanner';
import {readProjectDraft, writeProjectDraft} from "../projects/projectDraft";
import {createEmptyProyecto} from "../projects/emptyProyecto";
import { toFormDTO } from '../projects/projectFormUtils';
import {NotificationsProvider, useNotifications} from '../../../ui/notifications';
import { useTranslation } from 'react-i18next';
import { slugify } from '../../shared/utils/slugify';
import { ensureArray } from '../../shared/utils/api';
import { handleError } from '../../../utils/errorHandler';


const getSections = (t: (key: string) => string): ProjectFormSectionNavItem[] => [
    {id: 'informacion-general', label: t('projectForm.sections.informacion-general')},
    {id: 'requerimientos', label: t('projectForm.sections.requerimientos')},
    {id: 'autorizaciones', label: t('projectForm.sections.autorizaciones')},
    {id: 'contratos', label: t('projectForm.sections.contratos')},
    {id: 'datos-contables', label: t('projectForm.sections.datos-contables')},
    {id: 'seguimiento-plataforma', label: t('projectForm.sections.seguimiento-plataforma')},
    {id: 'seguimiento-coffe', label: t('projectForm.sections.seguimiento-coffe')},
    {id: 'certificaciones', label: t('projectForm.sections.certificaciones')},
    {id: 'hitos', label: t('projectForm.sections.hitos')},
    {id: 'participacion', label: t('projectForm.sections.participacion')},
    {id: 'informes', label: t('projectForm.sections.informes')},
    {id: 'comunicacion', label: t('projectForm.sections.comunicacion')},
    {id: 'reuniones', label: t('projectForm.sections.reuniones')},
    {id: 'plazos', label: t('projectForm.sections.plazos')},
    {id: 'acciones-seguimiento', label: t('projectForm.sections.acciones-seguimiento')},
];

export function AdminProjectEditPage() {
    const { slug } = useParams<{ slug: string }>();
    const { t } = useTranslation();

    const { data: projects, isLoading, isFetching } = useQuery<ProyectoDTO[]>({
        queryKey: ['admin', 'proyectos'],
        queryFn: () => proyectosApi.list(),
        select: (items: unknown) => ensureArray<ProyectoDTO>(items).map(p => ({...p, slug: slugify(p.informacionGeneral?.denominacion ?? '')})),
    });

    const isResolvingId = isLoading || isFetching;

    if (isResolvingId) {
        return (
            <div className="adminPageWrap">
                <div className="projectLoading">
                    <p>{t('adminProjectEdit.loadingProject')}</p>
                </div>
            </div>
        );
    }

    const proyecto = projects?.find((p: ProyectoDTO) => p.slug === slug);

    if (!proyecto || !proyecto.id) {
        return (
            <div className="adminPageWrap">
                <div className="projectLoading">
                    <h1>{t('adminProjectEdit.projectNotFound')}</h1>
                    <p>{t('adminProjectEdit.projectNotFoundHint')}</p>
                </div>
            </div>
        );
    }

    return (
        <NotificationsProvider>
            <SectionSaveCoordinatorProvider>
                <ProjectEditor proyectoId={proyecto.id} slug={slug!} />
            </SectionSaveCoordinatorProvider>
        </NotificationsProvider>
    );
}

function ProjectEditor(props: { proyectoId: number, slug: string }) {
    const { t } = useTranslation();
    const { proyectoId, slug } = props;
    const coordinator = useSectionSaveCoordinator();
    const notifications = useNotifications();
    const auth = useAuth();
    const usuarioId = auth.usuario?.id;
    const [isSaving, setIsSaving] = useState(false);

    const bloqueo = useBloqueoProyecto(proyectoId, auth.isAdmin ? usuarioId : undefined);
    const isReadOnly = !auth.isAdmin || !bloqueo.permitido;

    const projectMetaQ = useQuery({
        queryKey: ['admin', 'proyectos', 'meta', proyectoId],
        queryFn: async (): Promise<ProyectoDTO> => {
            const proyecto = await proyectosApi.get(proyectoId);
            return { ...proyecto, id: proyecto?.id ?? proyectoId };
        },
        enabled: !bloqueo.cargando,
    });

    const draftKey = `proyecto-${proyectoId}`;
    const queryClient = useQueryClient();
    useEffect(() => {
        if (projectMetaQ.data) {
            const currentDraft = readProjectDraft(draftKey) ?? createEmptyProyecto();
            const draftData = toFormDTO(projectMetaQ.data);
            writeProjectDraft(draftKey, { ...currentDraft, ...draftData });
        }
    }, [projectMetaQ.data, draftKey]);

    const isLoading = projectMetaQ.isLoading || !auth.usuario || bloqueo.cargando;

    if (isLoading) {
        return (
            <div className="adminPageWrap">
                <div className="projectLoading">
                    <p>{!auth.usuario ? t('adminProjectEdit.loadingUser') : (bloqueo.cargando ? t('adminProjectEdit.checkingLock') : t('adminProjectEdit.loadingProject'))}</p>
                </div>
            </div>
        );
    }

    if (projectMetaQ.isError) {
        window.alert(t('adminProjectEdit.criticalError', { error: projectMetaQ.error instanceof Error ? projectMetaQ.error.message : '' }));
        return null;
    }

    const SECTIONS = getSections(t);
    return (
        <div className="adminPageWrap">
            {auth.isAdmin && !bloqueo.permitido && (
                <LockBanner
                    nombreUsuario={bloqueo.nombreUsuario}
                    bloqueadoDesde={bloqueo.bloqueadoDesde}
                    intentarTomarControl={bloqueo.intentarTomarControl}
                />
            )}
            <ProjectReadOnlyProvider value={isReadOnly}>
                <ProjectFormLayout
                    proyecto={projectMetaQ.data}
                    sections={SECTIONS}
                    onSubmit={async () => {
                        if (isReadOnly) return;
                        setIsSaving(true);
                        try {
                            await proyectosApi.checkVersion(proyectoId, projectMetaQ.data?.version!);
                            await coordinator?.saveDirtySections();
                            await proyectosApi.update(proyectoId, { id: proyectoId, version: projectMetaQ.data?.version });
                            await queryClient.invalidateQueries({ queryKey: ['admin', 'proyectos', 'meta', proyectoId] });
                            await projectMetaQ.refetch();
                            notifications.showSuccess(t('adminProjectEdit.saveSuccess'));
                        } catch (e) {
                            handleError(e, { type: 'Save Project' });
                        } finally {
                            setIsSaving(false);
                        }
                    }}
                    busy={coordinator?.anySaving || isSaving}
                    submitDisabled={!coordinator?.anyDirty || isReadOnly}
                    submitLabel={t('adminProjectEdit.saveChanges')}
                    readOnly={isReadOnly}
                >
                    <div className="adminProjectForm">
                        <InformacionGeneralSection proyectoId={proyectoId} showIds={false}/>
                        <RequerimientosSection proyectoId={proyectoId} showIds={false}/>
                        <AutorizacionesSection proyectoId={proyectoId} showIds={false}/>
                        <DatosContratosSection proyectoId={proyectoId} showIds={false}/>
                        <DatosContablesSection proyectoId={proyectoId} projectSlug={slug} showIds={false}/>
                        <SeguimientoPlataformaSection proyectoId={proyectoId} projectSlug={slug} showIds={false}/>
                        <SeguimientoCoffeSection proyectoId={proyectoId} projectSlug={slug} showIds={false}/>
                        <CertificacionesDeObraSection proyectoId={proyectoId} projectSlug={slug} showIds={false}/>
                        <HitosYObjetivosSection proyectoId={proyectoId} showIds={false}/>
                        <ParticipacionSection proyectoId={proyectoId} showIds={false}/>
                        <InformesSection proyectoId={proyectoId} showIds={false}/>
                        <ComunicacionSection proyectoId={proyectoId} showIds={false}/>
                        <ReunionesSection proyectoId={proyectoId} showIds={false}/>
                        <PlazosSection proyectoId={proyectoId} showIds={false}/>
                        <AccionesSeguimientoSection proyectoId={proyectoId} showIds={false}/>
                    </div>
                </ProjectFormLayout>
            </ProjectReadOnlyProvider>
        </div>
    );
}
